<template>
  <div class="question-block">
    <div class="question-block__question">
      <div class="question-block__title">
        <drag-title-block class="question-block__drag sortable-handle handleClass" :title="title" />

        <div class="question-block__delete" :class="{ disabled: lastQuestion }" @click="deleteQuestion">
          <i class="icon icon-recycle_bin"></i>
        </div>
      </div>

      <div class="question-block__question-body">
        <vtabs v-model="type_of_question" :list="types_of_question" />

        <div class="question-block__question-content">
          <div class="question-block__question-left">
            <vcv-editor
              :id="'editor-question' + (index + 1)"
              ref="textarea"
              v-model="body"
              resizable
              auto_height
              :height="230"
              file-tooltip="tests.uploadFile"
              :toolbar-buttons="{
                bold: true,
                italic: true,
                underline: true,
                list: true,
                link: true,
                image: true,
                file: true,
                clean: true
              }"
              name="question"
              class="question-block__tab"
              :class="{ 'is-visible': type_of_question === 1 }"
            />

            <div
              class="question-block__question-video question-block__tab"
              :class="{ 'is-visible': type_of_question === 2 }"
            >
              <vbutton
                v-if="!video_player_src"
                class="vacancy-button vacancy-button__video"
                :disabled="!!youtube_link"
                label="vacancy.video.questions.video.record.button.label"
                @click.stop="toggle_record_modal(true)"
              />

              <div class="video-record">
                <div v-if="video_player_src" class="video-record-actions">
                  <div class="video-record-action" @click.stop="toggle_record_modal(true)">
                    {{ $tr('vacancy.videointerview.video.edit') }}
                  </div>

                  <div class="video-record-action" @click.stop="toggle_remove_video_modal(true)">
                    {{ $tr('vacancy.videointerview.video.delete') }}
                  </div>
                </div>

                <div v-if="!video_player_src" class="video-container-wrap">
                  <video-container
                    :placeholder="$tr('vacancy.video.questions.video.record.input.placeholder')"
                    :value="youtube_link"
                    :title="title"
                    @setVideoLink="setVideoLink"
                    @set_title="set_title"
                    @has_error="set_youtube_error"
                    @show_modal="toggle_youtube_modal(true)"
                  />
                </div>

                <div v-if="video_player_src || is_show_record_modal" class="video-descr">
                  <div class="preview-img" @click="toggle_record_modal(true)">
                    <video
                      v-if="saved_blob || (tempBlob && saved) || !preview_img"
                      class="canvas-img"
                      :src="saved_blob || tempBlob"
                    ></video>

                    <img v-else-if="preview_img" :src="preview_img" class="canvas-img" />

                    <div v-if="model && model.video && model.video.duration" class="duration-block">
                      {{ (model.video.duration / 1000) | mm_ss }}
                    </div>
                  </div>

                  <div class="input-block">
                    <input
                      v-model="video_title"
                      type="text"
                      class="form-control vcv-input"
                      :class="{ error: hasVideoDescrError }"
                      :placeholder="$tr('vacancy.videointerview.video.title')"
                    />
                    <label v-if="hasVideoDescrError" class="helper">
                      {{ $tr('common.requiredField') }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="question-block__question-right">
            <div class="row-block">
              <div class="to">
                <div class="prefix">{{ $tr('vacancy.videoInterview.timeForPreparation') }}</div>

                <masked-input
                  v-if="!unlimitedPrepareTime"
                  v-model="preparationTime"
                  type="text"
                  placeholder="01:30"
                  class="form-control vcv-input"
                  :mask="[/[0-6]/, /[0-9]/, ':', /[0-5]/, /[0-9]/]"
                  keep-char-positions
                  :disabled="retryable"
                />

                <input v-else type="text" class="form-control vcv-input" disabled />
              </div>

              <div class="from">
                <div class="prefix">{{ $tr('vacancy.videoInterview.timeForAnswer') }}</div>
                <masked-input
                  ref="answer_time"
                  v-model="answerTime"
                  v-tooltip="{
                    content: $tr('vacancy.video.timeLimit'),
                    placement: 'top-center',
                    classes: 'tight',
                    trigger: 'manual',
                    show: maskLimit
                  }"
                  type="text"
                  placeholder="02:30"
                  class="form-control vcv-input"
                  :mask="[/[0-2]/, /[0-9]/, ':', /[0-5]/, /[0-9]/]"
                  keep-char-positions
                />
              </div>
            </div>

            <div v-on-clickaway="hideOptions" class="options-dropdown">
              <div class="options-dropdown__label" :class="{ 'is-active': isOptionsVisible }" @click="toggleOptions">
                {{ $tr('responses.resume.questionnaire.additional') }}
                <img src="/static/images/elements/module-arrow-right.svg" />
              </div>

              <transition name="modal">
                <div v-if="isOptionsVisible" class="options-dropdown__select">
                  <div class="options-dropdown__select-option">
                    <vchecklabel v-model="isRequired" :label="$tr('vacancy.videoInterview.mandatory')" />
                  </div>

                  <div class="options-dropdown__select-option">
                    <vchecklabel v-model="retryable" :label="$tr('vacancy.videoInterview.retryable')" />
                  </div>

                  <div class="options-dropdown__select-option">
                    <vchecklabel
                      v-model="unlimitedPrepareTime"
                      :label="$tr('vacancy.videoInterview.unlimitedPrepareTime')"
                      :disabled="retryable === true"
                    />
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal v-if="is_show_youtube_modal" redesign is_big :has-footer="false" @close="toggle_youtube_modal(false)">
      <div slot="header">
        <div class="modal-video-title">{{ title }}</div>
      </div>

      <div slot="body">
        <div v-if="youtube_link" class="modal-video">
          <div class="modal-video-container">
            <iframe
              width="100%"
              height="100%"
              :src="youtube_link"
              frameborder="0"
              allow="autoplay; encrypted-media"
              webkitallowfullscreen
              mozallowfullscreen
              allowfullscreen
            ></iframe>
          </div>
        </div>
      </div>
    </modal>

    <modal v-if="is_show_record_modal" redesign is_big :has-footer="false" @close="cancel_record_modal(false)">
      <div v-if="!is_bad_browser" slot="header">
        <div class="modal-video-title">{{ title }}</div>
      </div>

      <div slot="body">
        <div class="modal-video">
          <vacancy-video-record
            :id="'canvas-element-' + model.id"
            class="vacancy-video-record"
            :video_player_src="video_player_src"
            :blob="saved_blob"
            :is_retake="is_retake"
            :disable_save_video="disable_save_video"
            @complete_recording="complete_recording"
            @startRecording="startRecording"
            @retakeTestVideo="retakeTestVideo"
            @save="save_video"
          />
        </div>
      </div>
    </modal>

    <modal-remove
      v-if="is_remove_video_modal_visible"
      @remove="handle_remove_record"
      @cancel="toggle_remove_video_modal(false)"
    />
  </div>
</template>

<script>
import axios from 'axios';
import debounce from 'debounce';
import { mixin as clickaway } from 'vue-clickaway';
import MaskedInput from 'vue-text-mask';
import isMobile from 'ismobilejs';
import IEBrowserDetector from '@/components/mixin/IEBrowserDetector.js';
import SafariBrowserDetector from '@/components/mixin/SafariBrowserDetector.js';
import { parseResponse } from '@/_helpers/utils.js';
import VideoContainer from '@/components/shared/VideoContainer.vue';
import Modal from '@/components/Modal.vue';
import VacancyVideoRecord from '../components/vacancy_video_record.vue';
import modalRemove from '../components/video_remove_modal.vue';
import DragTitleBlock from '@/routes_new/inner/vacancy/components/DragTitleBlock';

export default {
  name: 'VacancyVideointerviewQuestionsRow',

  components: {
    MaskedInput,
    VideoContainer,
    Modal,
    VacancyVideoRecord,
    modalRemove,
    DragTitleBlock
  },

  mixins: [IEBrowserDetector, SafariBrowserDetector, clickaway],

  props: {
    model: Object,
    index: Number,
    questionsLength: Number,
    vacancyId: {
      type: [String, Number],
      default: ''
    }
  },

  data() {
    return {
      isMobile,
      show_record: false,
      isDirty: false,
      preparation_time_default: '00:30',
      type_of_question: 1,
      has_youtube_error: false,
      disable_save_video: false,
      types_of_question: [
        {
          name: this.$tr('vacancy.video.questions.tab.text_field'),
          id: 1,
          disabled: false,
          disabled_text: this.$tr('vacancy.video.questions.tab.textDisabled')
        },
        {
          name: this.$tr('vacancy.video.questions.tab.video_field'),
          id: 2,
          disabled: false,
          disabled_text: this.$tr('vacancy.video.questions.tab.videoDisabled')
        }
      ],

      youtube: null,
      video_source: null,
      is_show_youtube_modal: false,
      is_show_record_modal: false,
      blob: null,
      tempBlob: null,
      saved_blob: null,
      video_img: null,
      videoId: null,
      is_remove_video_modal_visible: false,
      is_retake: false,
      saved: false,
      isOptionsVisible: false,
      maskLimit: false
    };
  },

  computed: {
    title: {
      get() {
        return `${this.$tr('videoInterview.questionCounter.question')} ${this.$tr('common.number')}${this.index + 1}`;
      },

      set() {
        return `${this.$tr('videoInterview.questionCounter.question')} ${this.$tr('common.number')}${this.index + 1}`;
      }
    },

    body: {
      get() {
        return this.model.body;
      },

      set(val) {
        // Call only if change
        if (val !== this.body) {
          this.updateModel({
            body: val
          });
        }
      }
    },

    hasVideoDescrError() {
      return !this.video_title;
    },

    unsaved_changes() {
      return this.$store.getters['vacancy/videointerview/haveUnsavedChanges'] || false;
    },

    has_video() {
      return this.model.media || this.youtube_link || this.blob || this.video_player_src;
    },

    retryable: {
      get() {
        return this.model.retryable;
      },

      set(val) {
        this.updateModel({
          retryable: val
        });
        this.unlimitedPrepareTime = val;
      }
    },

    answerTime: {
      get() {
        return this.formatTimeValue(this.model.answer_time);
      },

      set(val) {
        this.changeVal(val);
      }
    },

    preparationTime: {
      get() {
        if (
          this.formatTimeValue(this.model.preparation_time) !== '00:30' &&
          this.formatTimeValue(this.model.preparation_time) !== '00:00'
        ) {
          this.preparation_time_default = this.formatTimeValue(this.model.preparation_time);
        }

        return this.formatTimeValue(this.model.preparation_time);
      },

      set(val) {
        // Call only if change
        if (val !== this.preparationTime) {
          let cleanVal = this.cleanTimeValue(val);

          if (!isNaN(cleanVal)) {
            this.updateModel({
              preparation_time: cleanVal
            });

            if (val !== '00:00') {
              this.preparation_time_default = val;
            }
          }
        }
      }
    },

    isRequired: {
      get() {
        return this.model.is_required;
      },

      set(val) {
        this.updateModel({
          is_required: val
        });
      }
    },

    unlimitedPrepareTime: {
      get() {
        return this.preparationTime === '00:00';
      },

      set(val) {
        if (val === this.unlimitedPrepareTime) {
          return;
        }

        if (val) {
          this.preparationTime = '00:00';
        } else {
          this.preparationTime = this.preparation_time_default;
        }
      }
    },

    lastQuestion() {
      return this.questionsLength === 1;
    },

    error() {
      if (!this.body) {
        return this.$tr('videoInterview.questions.error.emptyQuestion');
      }

      return '';
    },

    videointerview() {
      return this.$store.getters['vacancy/videointerview/one'];
    },

    youtube_link: {
      get() {
        if (this.youtube !== null) {
          return this.youtube;
        }

        if (!this.model || !this.model.video_url) {
          return '';
        }
        return this.model.video_url;
      },

      set(val) {
        this.youtube = val;
        if (val) {
          this.video_player_src = '';
          this.saved_blob = null;
          this.tempBlob = null;
        } else {
          this.set_youtube_error(false);
        }

        this.updateModel({
          video_url: val ? val : null
        });
      }
    },

    video_id: {
      get() {
        if (this.videoId !== null) {
          return this.videoId;
        }

        if (!this.model || !this.model.video || !this.model.video.source) {
          return '';
        }
        return this.model.video.source;
      },

      set(val) {
        this.videoId = val;
        if (val) {
          this.youtube_link = '';
        }

        this.updateModel({
          video: {
            id: val
          }
        });
      }
    },

    video_player_src: {
      get() {
        if (this.video_source !== null) {
          return this.video_source;
        }

        if (!this.model || !this.model.video || !this.model.video.source) {
          return '';
        }
        return this.model.video.source;
      },

      set(val) {
        this.video_source = val;
        if (!val) {
          this.updateModel({
            video: null
          });
        }
      }
    },

    video_title: {
      get() {
        if (this.title !== null) {
          return this.title;
        }

        if (!this.model || !this.model.video_title) {
          return '';
        }
        return this.model.video_title;
      },

      set(val) {
        this.title = val;

        if (!this.unsaved_changes && this.model && this.model.video_title === val) {
          return;
        }

        this.updateModel({
          video_title: val
        });
      }
    },

    preview_img: {
      get() {
        return this.video_img || '';
      },

      set(val) {
        this.video_img = val;
      }
    },

    is_bad_browser() {
      const bad_browser = !this.isMobile ? this.isIE11OrOlder || this.isSafari || this.isEdge : false;

      return bad_browser && !this.video_player_src;
    }
  },

  watch: {
    body: {
      immediate: true,
      handler(val) {
        let clear_val = val.replace(/(<([^>]+)>)/gi, '').trim();

        if (this.type_of_question === 2) {
          return;
        }

        const new_types_of_question = this.types_of_question.map(type => {
          let disabled = false;
          if (type.id === 1) {
            disabled = clear_val ? false : !!this.has_video;
          } else {
            disabled = !!clear_val;
          }
          return Object.assign({}, type, {
            disabled
          });
        });

        this.types_of_question.splice(0, this.types_of_question.length, ...new_types_of_question);
      }
    },

    has_video: {
      immediate: true,
      handler(val) {
        if (this.type_of_question === 1) {
          return;
        }

        const new_types_of_question = this.types_of_question.map(type => {
          let disabled = false;
          if (type.id === 2) {
            disabled = val ? false : !!this.body;
          } else {
            disabled = !!val;
          }
          return Object.assign({}, type, {
            disabled
          });
        });

        this.types_of_question.splice(0, this.types_of_question.length, ...new_types_of_question);
      }
    },

    questionsLength() {
      if (this.model) {
        if (this.model.video && this.model.video.preview) {
          this.preview_img = this.model.video.preview;
        }
        if (this.model.video || this.model.video_url) {
          this.type_of_question = 2;
        } else {
          this.type_of_question = 1;
        }
      }
    }
  },

  mounted() {
    this.isDirty = false;

    if (this.model) {
      if (this.model.video && this.model.video.preview) {
        this.preview_img = this.model.video.preview;
      }
      if (this.model.video || this.model.video_url) {
        this.type_of_question = 2;
      }
    }
  },

  methods: {
    set_show_record(v) {
      this.show_record = v;
    },

    saveQuestion() {
      if (this.isDirty === true) {
        this.$store.dispatch('vacancy/videointerview/saveQuestion', {
          model: this.model
        });
        this.isDirty = false;
      }
    },

    toggleOptions() {
      this.isOptionsVisible = !this.isOptionsVisible;
    },

    hideOptions() {
      this.isOptionsVisible = false;
    },

    set_youtube_error(val) {
      this.has_youtube_error = val;
      this.updateModel(
        {
          _youtube_error: val
        },
        {
          ignore_changes: true
        }
      );
    },

    toggle_remove_video_modal(val) {
      this.is_remove_video_modal_visible = val;
    },

    set_title(val) {
      this.video_title = val;
    },

    toggle_youtube_modal(val) {
      this.is_show_youtube_modal = val;
    },

    toggle_record_modal(val) {
      this.is_show_record_modal = val;

      if (!val) {
        this.is_retake = false;
        this.disable_save_video = false;
      }
    },

    cancel_record_modal() {
      this.toggle_record_modal(false);
      this.blob = null;
    },

    setVideoLink(val) {
      if (this.youtube_link === val) {
        return;
      }
      this.youtube_link = val;
    },

    deleteQuestion() {
      if (this.lastQuestion) {
        return;
      }
      this.$store.commit('vacancy/videointerview/deleteQuestion', {
        model: this.model
      });
    },

    handle_remove_record() {
      this.video_player_src = null;
      this.youtube_link = null;
      this.video_title = null;
      this.blob = null;
      this.saved_blob = null;
      this.tempBlob = null;
      this.toggle_remove_video_modal(false);
      this.updateModel({
        video: null
      });
    },

    updateModel(data, params) {
      this.isDirty = true;
      if (params && params.ignore_changes) {
        return;
      }
      this.$store.commit('vacancy/videointerview/updateQuestion', {
        model: this.model,
        data: data
      });
    },

    formatTimeValue(minutes) {
      return minutes ? this.$moment.duration(minutes, 'seconds').format('mm:ss', { trim: false }) : '00:00';
    },

    cleanTimeValue(value) {
      let time = value.split(':');

      return +time[0] * 60 + +time[1];
    },

    complete_recording(data) {
      this.video_title = this.title;
      this.blob = data.blob;
      this.tempBlob = data.tempBlob ? data.tempBlob : null;
      this.is_retake = false;
    },

    fetch() {
      this.vacancyId &&
        this.$store.dispatch('vacancy/videointerview/fetch', {
          vacancyId: this.vacancyId
        });
    },

    startRecording() {
      this.saved = false;
    },

    retakeTestVideo() {
      this.is_retake = true;
    },

    save_video() {
      let data = new FormData();
      data.append('video[source]', this.blob);
      this.is_retake = false;
      this.disable_save_video = true;
      if (!this.blob) {
        this.toggle_record_modal(false);
        this.disable_save_video = false;
        return;
      }

      axios
        .post(`/1.0/video/save-video`, data)
        .then(response => {
          const data = parseResponse(response).data[0];
          this.video_player_src = data.source;
          this.video_id = data.id;
          this.toggle_record_modal(false);
          this.saved_blob = URL.createObjectURL(this.blob);
          this.disable_save_video = false;
          this.$noty.success(
            this.$tr('vacancy.video.questions.videoDownload', {
              number: this.index + 1
            })
          );
        })
        .catch(() => {
          this.saved = true;
          this.disable_save_video = false;
        });
    },

    changeVal: debounce(function (val) {
      if (val === this.answerTime) {
        return;
      }
      if (val[0] === '2') {
        const value = '20:00';
        val = value;
        this.$refs.answer_time.$refs.input.value = value;
        this.maskLimit = true;
        setTimeout(() => {
          this.maskLimit = false;
        }, 2500);
      }
      // Call only if change
      if (val !== this.answerTime) {
        let cleanVal = this.cleanTimeValue(val);

        if (!isNaN(cleanVal)) {
          this.updateModel({
            answer_time: cleanVal
          });
        }
      }
    }, 300)
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.question-block {
  box-shadow: $box-shadow;
  margin-bottom: 20px;
  border-radius: 4px;
  border: 1px solid $grey-light;

  &__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid $grey-light;
  }

  &__drag {
    flex: 1;
  }

  &__delete {
    width: 47px;
    height: 47px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 24px;
    color: $red;
    background: $background;
    transition: all ease-in-out 0.3s;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:hover {
      background: $red-light;
      color: $white;
      transition: all 0.3s;
    }
  }

  &__tab {
    height: 0;
    position: absolute;
    opacity: 0;
    visibility: hidden;
    transition: opacity ease-in-out 0.4s;

    &.is-visible {
      height: auto;
      position: relative;
      opacity: 1;
      visibility: visible;
      transition: opacity ease-in-out 0.4s;
    }
  }

  &__question-body {
    padding: 10px 20px 0;
    background: $white;
  }

  &__question-content {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: space-between;
  }

  &__question-left {
    position: relative;
  }

  &__question-right {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 10px 0 20px;
    width: auto;
    flex: 1;

    .row-block {
      align-self: flex-start;
      display: inline-flex;
      margin-top: 0;
    }

    .prefix {
      @include mixin-text-main;
      margin-bottom: 4px;
    }

    input {
      @include mixin-text-main;
      padding: 9px 9px 8px;
      text-align: center;
      max-width: 63px;
    }

    .row:first-child .to {
      margin-left: 0;
    }

    .to,
    .from {
      display: flex;
      align-items: center;
      margin-left: 0;
      margin-right: 20px;

      .prefix {
        margin-right: 10px;
      }
    }
  }

  &__question-video {
    padding: 10px 0;

    .canvas-img {
      height: 64px;
      width: 84px;
      min-width: 84px;
      margin-right: 15px;
      border-radius: 4px;
      transform: scale(-1, 1);
    }

    .video-container-wrap {
      margin-top: 20px;
    }

    .video-record-actions {
      margin-bottom: 24px;
      display: flex;
      align-items: center;
    }

    .video-record-action {
      margin-right: 40px;
      font-style: normal;
      font-weight: 500;
      font-size: 15px;
      line-height: 22px;
      letter-spacing: 0.005em;
      color: #9faab6;
      cursor: pointer;
    }

    .video-descr {
      display: flex;
      align-items: center;

      input {
        width: 100%;
        flex: 1;
      }
    }

    .input-block {
      position: relative;
      display: flex;
      width: 100%;
      flex-direction: column;
      align-items: flex-start;
    }

    .helper {
      position: absolute;
      top: 33px;
      left: 0;
      display: block;
      margin: 0;
      color: #a6a6a6;
      line-height: 20px;
      font-size: 12px;
      color: $red;
      padding-top: 5px;
    }
  }
}
.input-block {
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
}
.helper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

// Modals
.modal-video {
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 -50px 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-video-title {
  font-style: normal;
  font-weight: 500;
  font-size: 28px;
  line-height: 41px;
  letter-spacing: 0.005em;
  color: #233346;
  margin-bottom: 20px;
}

.vacancy-video-record {
  width: 100%;
}

// Dropdown select
.options-dropdown {
  position: relative;

  &__label {
    @include mixin-text-main;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 15px;
    border: 1px solid #d2d6e0;
    background: $white;
    box-sizing: border-box;
    border-radius: 2px;
    min-width: 175px;
    cursor: pointer;

    img {
      transition: all ease-in-out 0.4s;
      transform: rotate(90deg);
    }

    &.is-active {
      img {
        transition: all ease-in-out 0.4s;
        transform: rotate(270deg);
      }
    }
  }

  &__select {
    position: absolute;
    background: #fff;
    border: 1px solid #d2d6e0;
    box-shadow: $box-shadow;
    border-radius: 2px;
    right: 0;
    top: 45px;
    padding: 15px;
    z-index: 2;
    transition: all ease-in-out 0.4s;

    ::v-deep {
      .vcheckbox_label,
      .checkbox_label {
        white-space: nowrap;
      }
    }
  }

  &__select-option {
    margin-bottom: 18px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
